import React, { useState, useMemo } from 'react';
import IconPicker from '../../../IconPicker';
import { settingsFunctions } from '../../../../functions/settings';
import PhoneInput, { formatPhoneNumberIntl } from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import ButtonPreview from '../ButtonPreview';
import { useAuth } from '../../../../utils/Auth';
import styles from './ButtonEditor.module.css';

function ButtonEditor(props) {
    const { setShowForm, buttons, setButtons, buttonToEdit, uid, accountType, setButtonToEdit, profile_theme } = props;
    const { updateVcardObject, buttonsActiveState, buttonRefArr, getButtonLink } = settingsFunctions;
    const  { getUpdatedUserVcard } = useAuth();
    const activeButtons = useMemo(() => buttons.filter(btn => btn.active).length || 0, [buttons]);
    
    const [ editObj, setEditObj ] = useState({
        active: buttonToEdit?.active,
        data: buttonToEdit?.data ? buttonToEdit.data : '',
        hasChanges: false,
        icon: buttonToEdit?.icon,
        index: Array.isArray(buttons) && buttons?.length ? buttons?.findIndex(btn => btn?.data === buttonToEdit?.data) : -1,
        label: buttonToEdit?.label ? buttonToEdit.label : '',
        ref: buttonToEdit?.ref,
    })
    const [ deleteConfirm, setDeleteConfirm ] = useState(false);
    const [ showSpinner, setShowSpinner] = useState(false);
    
    const handleInput = e => {

        const newData = editObj.ref === 'phone' ? e : e.target.value;
        const hasChanges = editObj.ref === 'phone' 
            ? formatPhoneNumberIntl(e) !== buttonToEdit?.data 
            : e.target.value !== buttonToEdit?.data;

        setEditObj(prev => ({
            ...prev,
            data: newData,
            hasChanges: hasChanges
        }));
    };

    const handleCheck = e => {
        const hasChanges = e.target.checked !== !buttonToEdit.active;
        setEditObj(prev => ({
            ...prev, 
            active: !e.target.checked,
            hasChanges: hasChanges
        }));
    };

    const handleTrash = async () => {
        setShowSpinner(true);
        try {
            const newArr = buttons.filter(btn => btn.data !== buttonToEdit.data);
            const update = { buttons: accountType === 'basic' ? buttonsActiveState(newArr) : newArr };
            
            setButtons(update.buttons);

            await updateVcardObject(update, uid);
            await getUpdatedUserVcard();
        } catch (error) {
            console.error("Error deleting button:", error);
        } finally {
            setShowSpinner(false);
            setShowForm('');
            setEditObj(prev => ({ ...prev, hasChanges: false }));
            setButtonToEdit('');
        }
    };

    const resetForm = () => {
        setEditObj({
            active: buttonToEdit?.active,
            data: buttonToEdit?.data || '',
            hasChanges: false,
            icon: buttonToEdit?.icon,
            index: buttons.findIndex(btn => btn?.data === buttonToEdit?.data),
            label: buttonToEdit?.label || '',
            ref: buttonToEdit?.ref,
        });
    };

    const handleIcon = (icon) => {
        if (icon === buttonToEdit?.icon) {
            setEditObj(prev => ({ ...prev, hasChanges: prev?.hasChanges || false }));
        } else {
            setEditObj(prev => ({ ...prev, icon: icon, hasChanges: true }));
        };
    };

    const saveButtonEdits = async () => {
        setShowSpinner(true);
        try {
            const btnUpdate = {
                active: editObj.active,
                added: buttonToEdit.added,
                data: editObj.ref === 'phone' 
                    ? formatPhoneNumberIntl(editObj.data) 
                    : buttonRefArr.includes(editObj.ref) 
                        ? getButtonLink(editObj.ref, editObj.data.replace(/\/$/,'')) 
                        : editObj.data.replace(/\/$/,''),
                icon: editObj.icon,
                label: editObj.label || '',
                ref: editObj.ref,
                updated: Date.now()
            };

            const tempButtonArr = [...buttons];
            tempButtonArr[editObj.index] = btnUpdate;

            const update = { buttons: accountType === 'basic' ? buttonsActiveState(tempButtonArr) : tempButtonArr };

            setButtons(update.buttons);
                        
            await updateVcardObject(update, uid);
            await getUpdatedUserVcard();
        } catch (error) {
            console.error("Error saving button edits:", error);
        } finally {
            setShowSpinner(false);
            setShowForm('');
        };
    };

    const renderForm = () => (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.backBtn} onClick={() => setShowForm("")}>
                    <i className='fas fa-arrow-left'></i>
                    Back
                </div>
                Edit Button
                <div className={styles.resetBtn} onClick={resetForm}>
                    Reset
                </div>
            </div>
            <ButtonPreview reference={editObj.ref} data={editObj.data} icon={editObj.icon} label={editObj.label} profile_theme={profile_theme} />
            <div className={styles.inputGroup} style={{marginTop: '1.5rem'}}>
                <input
                    type='text'
                    id="edit-button-label"
                    className={styles.input}
                    value={editObj.label}
                    onChange={e => setEditObj(prev => ({...prev, label: e.target.value, hasChanges: e.target.value !== buttonToEdit?.label}))}
                    placeholder="Create a custom label for your button!"
                    maxLength="25"
                />
                <label htmlFor='edit-button-label' className={styles.label}>Label (Optional)</label>
            </div>
            {editObj.ref === 'phone' ? (
                <div className={styles.inputGroup}>
                    <PhoneInput
                        id='edit-button-phone'
                        international
                        className={styles.phoneInput}
                        countryCallingCodeEditable={false}
                        defaultCountry='US'
                        value={editObj.data.replaceAll(' ','')}
                        onChange={handleInput}
                    />
                    <label htmlFor='edit-button-phone' className={styles.phoneLabel}>Phone</label>
                </div>
            ) : (
                <div className={styles.inputGroup}>
                    <input
                        type={buttonToEdit.ref === 'email' ? 'email' : buttonRefArr.includes(buttonToEdit.ref) ? 'text' : 'url'}
                        id='edit-button-default'
                        className={styles.input}
                        value={editObj.data}
                        onChange={handleInput}
                        pattern={buttonToEdit.ref !== 'email' && !buttonRefArr.includes(buttonToEdit.ref) ? "(^((https|http)://)?[a-z0-9]+.[a-z]+(/[a-zA-Z0-9#]+/?)*$)|^((https|http)://)?(www.)?[a-z0-9]+.[a-z]+(/[a-zA-Z0-9#]+/?)*$" : null}
                    />
                    <label htmlFor='edit-button-default' className={styles.label}>{`${buttonToEdit.ref.charAt(0).toUpperCase()}${buttonToEdit.ref.slice(1)}`}</label>
                </div>
            )}
            <div className={styles.inputGroup}>
                <IconPicker handleIcon={handleIcon} prevIcon={buttonToEdit?.icon} />
            </div>
            <div className={styles.disableBtnWrap}>
                <input 
                    type='checkbox' 
                    id='edit-button-disable' 
                    defaultChecked={!editObj.active} 
                    onChange={handleCheck} 
                    disabled={accountType === 'basic' && (buttonToEdit.active === false && activeButtons > 4)}
                />
                <label htmlFor='edit-button-disable'>
                    <div htmlFor='edit-button-disable'><span /></div>
                    {editObj.active ? "Disable Button (Will NOT appear on your digital card)" : 'Disabled (Button is hidden)'}
                </label>
            </div>
            <div className={styles.inputGroup} style={{marginBottom: '0', justifyContent: 'center'}}>
                <button
                    onClick={saveButtonEdits} 
                    className={styles.saveBtn} 
                    disabled={!editObj.hasChanges || showSpinner}>
                    {showSpinner ? <i className="fa-thin fa-spinner fa-spin-pulse"></i> : 'Save'}
                </button>
                <div className={styles.trashWrap}>
                    <div className={styles.inputGroup}>
                        <button className={`${styles.deleteBtn} ${styles.confirm}`} onClick={handleTrash}><i className='far fa-trash-can' />Confirm</button>
                        <button className={`${styles.deleteBtn} ${styles.cancel}`} onClick={() => setDeleteConfirm(false)}>Cancel</button>
                    </div>
                    <button className={deleteConfirm ? `${styles.trashBtn} ${styles.confirm}` : styles.trashBtn} onClick={() => setDeleteConfirm(true)} >
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );

    return renderForm();
};

export default ButtonEditor;